'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import Link from 'next/link'

import InscriptionContent from '@/components/inscription-content'
import SetPfpButton from '@/components/set-pfp-button'
import SetSnsButton from '@/components/set-sns-button'
import RarityPills from '@/components/rarity-pills-blur'

import { Inscription } from '@/utils/types/inscription'
import { NAMES_SLUGS } from '@/utils/constants'
import formatHiddenChars from '@/utils/format-hidden-chars'

import PlusCircle from '@/icons/wallet-plus-circle'
import CheckCircle from '@/icons/wallet-check-circle'
import styles from './style.module.css'
import isFungible from '@/utils/is-fungible'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'

const InscriptionCard: FC<{
	inscription: Inscription
	footer?: React.ReactNode
	showSetPfp?: boolean
	showSetSns?: boolean
	subtitle?: string
	onClick: (evt: any) => void
	selected: boolean
}> = (props): ReactElement => {
	const { subtitle, onClick, selected } = props
	const { id, num, content_type, collection, meta } = props.inscription
	const [_openWarning, _setOpenWarning] = React.useState(false)

	const [hover, setHover] = React.useState(false)

	const handleMouseEnter = React.useCallback(() => setHover(true), [])
	const handleMouseLeave = React.useCallback(() => setHover(false), [])

	const href = `/inscription/${id}`
	const name = formatHiddenChars(meta?.name as any)
	const openInscriptionModal = useInscriptionModal()

	const handleNameClick = (evt: React.MouseEvent) => {
		evt.stopPropagation()
		if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button === 1) return
		evt.preventDefault()
		openInscriptionModal({ id, inscription: props.inscription })
	}

	return (
		<div
			className={classNames(
				'border rounded-3xl hover:border-[#B692F6] cursor-pointer overflow-hidden',
				{
					['border-[#B692F6]']: selected,
					['border-transparent']: !selected
				}
			)}
			onClick={onClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			role="button"
			tabIndex={0}
			onKeyDown={onKeyboardClick(onClick)}
		>
			<div
				className={`${styles.mask} size-48 overflow-hidden rounded-3xl relative overflow-hidden`}
			>
				<div className="h-full hover:scale-110 rounded-3xl transition duration-500 ease-in-out">
					<InscriptionContent
						className="size-48 roundex-3xl object-cover pointer-events-none inline"
						inscription={props.inscription}
						showPricePer={true}
					/>
				</div>
				{content_type?.includes('image') && props.showSetPfp && (
					<SetPfpButton
						inscription={props.inscription}
						className="size-6 absolute top-2 left-2 fill-[white]"
					/>
				)}
				{NAMES_SLUGS.includes(collection?.slug as any) && props.showSetSns && (
					<SetSnsButton
						inscription={props.inscription}
						className="size-6 absolute top-2 left-2 fill-[white]"
					/>
				)}
				{hover && !selected && (
					<PlusCircle className="size-6 absolute top-2 right-2 fill-[white]" />
				)}
				{selected && <CheckCircle className="size-6 absolute top-2 right-2 fill-[#B692F6]" />}

				{!isFungible(collection?.slug as any) && (
					<RarityPills className="absolute bottom-2 right-2" inscription={props?.inscription} />
				)}
			</div>
			<div className="flex flex-col p-3 w-48">
				<Link href={href} className="flex flex-col group" onClick={handleNameClick}>
					<p className="text-xs font-semibold group-hover:text-blue-700 dark:group-hover:text-blue-400 group-hover:underline transition-colors">
						{name ? name : `Inscription #${num}`}
					</p>
					{!subtitle && (
						<p className="text-xs text-zinc-500 flex items-center gap-1">
							{name ? `Inscription #${num}` : content_type}
						</p>
					)}
					{subtitle && <p className="text-xs text-zinc-500 flex items-center gap-1">{subtitle}</p>}
				</Link>
				{props.footer && (
					<div
						className="mt-2"
						onClickCapture={(evt) => {
							evt.stopPropagation()
							onClick(evt)
						}}
					>
						{props.footer}
					</div>
				)}
			</div>
		</div>
	)
}

export default InscriptionCard
